export const SELFIE_HANDOFF_UNAVAILABLE = 'SELFIE_HANDOFF_UNAVAILABLE' as const;

export const SELFIE_HANDOFF_INTERNAL_ERROR =
  'SELFIE_HANDOFF_INTERNAL_ERROR' as const;

export const SELFIE_HANDOFF_MESSAGE =
  'Verification is temporarily unavailable at this step. Please try again.';

export type SelfieArtifactStatus =
  | 'missing'
  | 'expired'
  | 'temporarily_unavailable';
export type RecoverableSelfieArtifactStatus = Exclude<
  SelfieArtifactStatus,
  'expired'
>;

export type SelfieHandoffRetryTarget = 'selfie_handoff' | 'selfie_capture';

export interface RecoverablePipelineErrorMessage {
  type: 'recoverable_error';
  code: typeof SELFIE_HANDOFF_UNAVAILABLE;
  message: string;
  retryable: true;
  terminal: false;
  retryTarget: SelfieHandoffRetryTarget;
  artifactStatus?: RecoverableSelfieArtifactStatus;
}

export interface TerminalSelfieHandoffErrorMessage {
  type: 'error';
  code: typeof SELFIE_HANDOFF_UNAVAILABLE;
  message: string;
  retryable: false;
  terminal: true;
  artifactStatus: 'expired';
}

export interface InternalSelfieHandoffErrorMessage {
  type: 'error';
  code: typeof SELFIE_HANDOFF_INTERNAL_ERROR;
  message: string;
  retryable: false;
  terminal: false;
  artifactStatus: 'internal_error';
}

export type SelfieHandoffErrorMessage =
  | RecoverablePipelineErrorMessage
  | TerminalSelfieHandoffErrorMessage
  | InternalSelfieHandoffErrorMessage;

export const isSelfieHandoffUnavailableMessage = (
  value: unknown
): value is RecoverablePipelineErrorMessage => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const message = value as Record<string, unknown>;
  return (
    message.type === 'recoverable_error' &&
    message.code === SELFIE_HANDOFF_UNAVAILABLE &&
    message.retryable === true &&
    message.terminal === false &&
    (message.artifactStatus === undefined ||
      message.artifactStatus === 'missing' ||
      message.artifactStatus === 'temporarily_unavailable') &&
    (message.retryTarget === 'selfie_handoff' ||
      message.retryTarget === 'selfie_capture')
  );
};

export const isSelfieHandoffErrorMessage = (
  value: unknown
): value is SelfieHandoffErrorMessage => {
  if (isSelfieHandoffUnavailableMessage(value)) {
    return true;
  }
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const message = value as Record<string, unknown>;
  const isTerminalExpiry =
    message.type === 'error' &&
    message.code === SELFIE_HANDOFF_UNAVAILABLE &&
    message.retryable === false &&
    message.terminal === true &&
    message.artifactStatus === 'expired';
  const isInternalFailure =
    message.type === 'error' &&
    message.code === SELFIE_HANDOFF_INTERNAL_ERROR &&
    message.retryable === false &&
    message.terminal === false &&
    message.artifactStatus === 'internal_error';

  return isTerminalExpiry || isInternalFailure;
};

export class SelfieHandoffUnavailableError extends Error {
  readonly code = SELFIE_HANDOFF_UNAVAILABLE;
  readonly retryable = true;
  readonly terminal = false;

  constructor(
    readonly artifactStatus: RecoverableSelfieArtifactStatus,
    readonly retryTarget: SelfieHandoffRetryTarget,
    readonly attempts: number
  ) {
    super(SELFIE_HANDOFF_MESSAGE);
    this.name = 'SelfieHandoffUnavailableError';
  }
}

export class SelfieHandoffFetchError extends Error {
  readonly code = SELFIE_HANDOFF_INTERNAL_ERROR;
  readonly retryable = false;
  readonly terminal = false;
  readonly artifactStatus = 'internal_error' as const;

  constructor(readonly status?: number) {
    super('Unable to continue verification right now. Please try again.');
    this.name = 'SelfieHandoffFetchError';
  }
}

export class SelfieHandoffExpiredError extends Error {
  readonly code = SELFIE_HANDOFF_UNAVAILABLE;
  readonly retryable = false;
  readonly terminal = true;
  readonly artifactStatus = 'expired' as const;
  readonly retryTarget = null;

  constructor(readonly status: number = 410) {
    super('This verification has expired and can no longer continue.');
    this.name = 'SelfieHandoffExpiredError';
  }
}

export class PipelineRecoveryExhaustedError extends Error {
  readonly code = SELFIE_HANDOFF_UNAVAILABLE;
  readonly retryable = true;
  readonly terminal = false;

  constructor(readonly attempts: number) {
    super(SELFIE_HANDOFF_MESSAGE);
    this.name = 'PipelineRecoveryExhaustedError';
  }
}
